(ns frontend.view.modals.options-management.view
    (:require
      [common.localization :as loc]
      [common.schema.registry :as registry]
      [frontend.db :as app-db]
      [frontend.state.current-session :as session]
      [frontend.ui.component.heading.view :refer [h3]]
      [frontend.ui.component.text.view :refer [text]]
      [frontend.ux-overhaul.table.view :as table]
      [frontend.view.modals.confirmation.view :as confirmation-modal]
      [frontend.view.modals.curtains.delete-option-curtain.view :as delete-option-curtain]
      [frontend.view.modals.curtains.merge-option-curtain.view :as merge-option-curtain]
      [frontend.view.modals.events :as modals]
      [frontend.view.modals.options-management.events :as events]
      [frontend.view.modals.options-management.subs :as subs]
      [oops.core :as oops]
      [re-frame.core :as rf]
      [reagent.core :as r]
      [ui.components.button.view :as button]
      [ui.components.modal.style :as modal-style]
      [ui.components.modal.view :as modal]
      [ui.components.toggle.view :refer [toggle]]))

(defonce modal-key :options-management-modal-key)

(defn viewport-warning-message
  []
  [:div {:class "pt-5"}
   [:div {:class "text-center"}
    [:div {:class "viewport-warning-message__card-body"}
     [:div {:class "text-center py-3"}
      [:img {:class "w-full h-auto"
             :src   "/img/not_compatible_for_mobile.svg"}]]
     [:div {:class "text-center"}
      [:p (loc/tr :viewport-warning-message-options-management)]
      [:p (loc/tr :viewport-warning-message-options-management-info)]]]]])

(defn field-options-empty-message
  []
  [:div {:class "py-5 my-5 text-center"}
   [:img {:class "w-full h-auto mb-3 max-w-[140px]"
          :src   "/img/undraw_No_data_re_kwbl.svg"}]
   [:strong {:class "block text-gray-800 text-[18px] font-bold my-3"}
    (loc/tr :no-field-options-added)]])

(defn info-body
  [selected-field-label]
  [:div
   {:class "flex-shrink-0 flex flex-col w-[375px] max-[1080px]:w-[200px]"}
   [:div {:class "p-3"}
    [:div {:class "flex items-baseline mb-3"}
     [:i {:class "fas fa-info-circle mr-2"}]
     [text
      {:text-size :xsmall}
      (loc/tr :options-management-info-text [selected-field-label])]]
    [:div {:class "mb-2"}
     [h3 (loc/tr :adding-options)]
     [text
      {:text-size :xsmall}
      (loc/tr :adding-options-info-text)]]
    [:div {:class "mb-2"}
     [h3 (loc/tr :archiving-options)]
     [text
      {:text-size :xsmall}
      (loc/tr :archiving-options-info-text)]]
    [:div {:class "mb-2"}
     [h3 (loc/tr :deleting-options)]
     [text
      {:text-size :xsmall}
      (loc/tr :deleting-options-info-text)]]
    [:div {:class "mb-2"}
     [h3 (loc/tr :merging-options)]
     [text
      {:text-size :xsmall}
      (loc/tr :merging-options-info-text)]]
    [:div {:class "mb-2"}
     [h3 (loc/tr :rearranging-options)]
     [text
      {:text-size :xsmall}
      (loc/tr :rearranging-options-info-text)]]]])

(defn field-options-toggle-item
  "Field options toggle item"
  [{:keys [checked? on-change disabled?]}]
  [:div
   {:class "max-w-[200px] h-full px-3 py-1 flex justify-center items-center text-white"}
   [toggle
    {:checked?  checked?
     :on-change on-change
     :disabled? disabled?}]])

(defn resolve-rows
  [named-values]
  (->> named-values
       (mapv (fn [{:keys [named-value/value db/id common/ord named-value/archived-in-pipelines common/archived?]}]
               {:named-value/value                 value
                :named-value/archived-in-pipelines archived-in-pipelines
                :common/ord                        ord
                :db/id                             id
                :common/archived?                  archived?}))))

(defn toggle-common-archived-cell-renderer
  [{:keys [row-data node]}]
  (let [index (oops/oget node "rowIndex")
        checked? (not (:archived? row-data))]
    [:div
     [field-options-toggle-item
      {:checked?        checked?
       :on-change       (fn [new-value]
                          (rf/dispatch [::events/update-named-value {:index     index
                                                                     :new-value (not new-value)
                                                                     :attribute :common/archived?}]))
       :disabled?       false
       :toggle-on-text  (loc/tr :active)
       :toggle-off-text (loc/tr :archived)}]]))

(defn toggle-pipeline-archived-cell-renderer
  [{:keys [row-data node pipeline-id]}]
  (let [archived-in-pipelines (:archived-in-pipelines row-data)
        index (oops/oget node "rowIndex")
        checked? (not (some #{pipeline-id} archived-in-pipelines))
        new-value (if checked?
                    (conj (set archived-in-pipelines) pipeline-id)
                    (disj (set archived-in-pipelines) pipeline-id))]
    [:div
     [field-options-toggle-item
      {:checked?  checked?
       :on-change (fn []
                    (rf/dispatch [::events/update-named-value {:index     index
                                                               :new-value new-value
                                                               :attribute :named-value/archived-in-pipelines}]))
       :disabled? false}]]))

(defn resolve-options-management-columns
  []
  (let [pipelines @(rf/subscribe [::subs/pipelines])]
    (concat
      [{:headerName      (loc/tr :options)
        :field           :named-value/value
        :resizable       true
        :rowDrag         true
        :editable        true
        :suppressMovable true}]
      (if (empty? pipelines)
        [{:headerName      (loc/tr :active-question)
          :field           :common/archived?
          :resizable       true
          :sortable        false
          :suppressMovable true
          :cellRenderer    (fn [props]
                             (let [data (js->clj (oops/oget props "data") {:keywordize-keys true})
                                   node (oops/oget props "node")]
                               (r/as-element
                                 (toggle-common-archived-cell-renderer
                                   {:row-data data
                                    :node     node}))))}]
        (->> pipelines
             (mapv (fn [pipeline]
                     (let [name (:pipeline/name pipeline)
                           id (:db/id pipeline)]
                       {:headerName      name
                        :field           :named-value/archived-in-pipelines
                        :resizable       true
                        :sortable        false
                        :suppressMovable true
                        :cellRenderer    (fn [props]
                                           (let [data (js->clj (oops/oget props "data") {:keywordize-keys true})
                                                 node (oops/oget props "node")]
                                             (r/as-element
                                               (toggle-pipeline-archived-cell-renderer
                                                 {:row-data    data
                                                  :node        node
                                                  :pipeline-id id}))))}))))))))

(defn options-management-tools
  [{:keys [pipelines]}]
  (let [selected-option-id @(rf/subscribe [::subs/selected-row-id])
        selected-option-ord @(rf/subscribe [::subs/selected-row-ord])
        selected-named-value @(rf/subscribe [::subs/selected-named-value])
        merge-targets @(rf/subscribe [::subs/merge-targets])
        delete-disabled? @(rf/subscribe [::subs/no-row-selected?])
        merge-disabled? @(rf/subscribe [::subs/merge-disabled?])]
    [:div
     {:class-name "flex items-center gap-3"}
     [button/helper-button
      {:on-click (fn [_]
                   (rf/dispatch [::events/create-new-draft-option]))}
      [:i.fal.fa-plus]
      (loc/tr :add-new-option)]
     [button/helper-button
      {:disabled? delete-disabled?
       :on-click  (fn [_] (if selected-option-id
                            (modals/open-modal!
                              delete-option-curtain/modal-key
                              {:named-value   selected-named-value
                               :on-completed! (fn [close-modal!]
                                                (rf/dispatch [::events/delete-option {:option-id selected-option-id
                                                                                      :ord       selected-option-ord}])
                                                (close-modal!))})
                            (rf/dispatch [::events/delete-option {:ord selected-option-ord}])))}
      [:i.fal.fa-trash]
      (loc/tr :delete)]
     [button/helper-button
      {:disabled? merge-disabled?
       :on-click  (fn [_] (modals/open-modal!
                            merge-option-curtain/modal-key
                            {:named-value   selected-named-value
                             :victim-id     selected-option-id
                             :targets       merge-targets
                             :pipelines     pipelines
                             :on-completed! (fn [close-modal!]
                                              (rf/dispatch [::events/merge-options {:victim-id selected-option-id}])
                                              (close-modal!))}))}
      [:i.fal.fa-code-merge]
      (loc/tr :merge)]]))

(defn modal-body
  [{:keys [pipelines state-atom selected-field-label]}]
  (let [named-values @(rf/subscribe [::subs/sorted-named-values])
        suppress-row-drag? @(rf/subscribe [::subs/row-dragging-disabled?])
        row-to-highlight @(rf/subscribe [::subs/row-to-highlight])
        initialized? @(rf/subscribe [::subs/initialized?])]
    (if (< (:width @app-db/window) 834)
      [viewport-warning-message]
      [:div
       {:class "flex flex-row flex-1"}
       [:div
        {:class "flex flex-col flex-1"}
        [:div
         {:class "pt-3"}
         [table/view
          {:grid-id                    "options-management-table"
           :state-atom                 state-atom
           :auto-height?               false
           :min-height                 "550px"
           :max-height                 "550px"
           :column-defs                (when initialized? @(r/track resolve-options-management-columns))
           :row-data                   (when initialized? @(r/track resolve-rows named-values))
           :get-row-class              (fn [params]         ;;To highlight when dragging the row
                                         (let [node (oops/oget params "node")
                                               old-index (oops/oget node "rowIndex")]
                                           (if (= old-index row-to-highlight)
                                             "bg-[#2196F319]" ""))) ;;TODO better way to set highlight color?
           :default-col-def            {:sortable                 true
                                        :suppressContextMenu      true
                                        :suppressHeaderMenuButton true
                                        :suppressColumnsToolPanel true
                                        :suppressFiltersToolPanel true
                                        :menuTabs                 ["generalMenuTab"]}
           :suppress-context-menu      true
           :width                      "100%"
           :row-selection              {:mode "singleRow"}
           :enable-cell-text-selection "false"
           :suppress-row-drag          suppress-row-drag?
           :on-row-drag-move           (fn [params] (rf/dispatch [::events/on-row-drag-move params]))
           :on-row-drag-end            (fn [params] (rf/dispatch [::events/reorder-on-drag-end params]))
           :on-cell-edit-request       (fn [params] (rf/dispatch [::events/update-named-value-on-cell-edit params]))
           :on-selection-changed       (fn [params] (rf/dispatch [::events/set-selected-row params]))
           :on-sort-changed            (fn [params]
                                         (rf/dispatch [::events/disable-row-dragging params]))
           :no-rows-overlay-component  (fn [] (r/as-element [field-options-empty-message]))
           :tools                      [:div
                                        {:class "flex gap-3 whitespace-nowrap bg-white p-2 z-0 border-solid border-t
                                          border-l border-r border-base-light-border-paler font-bold text-[14px] cursor-pointer"}
                                        [options-management-tools
                                         {:pipelines pipelines}]]}]]]
       [info-body selected-field-label]])))

(defn modal-footer
  [{:keys [unsaved-changes? close-modal!]}]
  (let [errors? @(rf/subscribe [::subs/errors?])
        error-messages @(rf/subscribe [::subs/error-messages])
        saving? @(rf/subscribe [::subs/saving?])]
    [:<>
     [:div
      {:class "flex gap-3 items-center"}
      [button/secondary-hollow
       {:on-click (fn []
                    (if unsaved-changes?
                      (modals/open-modal! confirmation-modal/modal-key
                                          {:body           [:p (loc/tr :you-have-unsaved-settings-confirm-body)]
                                           :title          (loc/tr :unsaved-changes)
                                           :cancel-text    (loc/tr :ignore-changes-and-exit)
                                           :confirm-text   (loc/tr :save-settings-and-exit)
                                           :skip-checkbox? true
                                           :on-confirm     (fn [_]
                                                             (if errors?
                                                               (events/open-error-modal! error-messages)
                                                               (rf/dispatch [::events/save-changes {:close-modal! close-modal!}])))
                                           :on-cancel      (fn [_]
                                                             (close-modal!))})
                      (close-modal!)))}
       (if (< (:width @app-db/window) 834)
         (loc/tr :close)
         (loc/tr :cancel))]]
     [:div
      {:class "flex gap-3 items-end"}
      [button/primary-solid
       {:tooltip   (when errors?
                     [:div.errors
                      (for [msg error-messages]
                        ^{:key (str msg)}
                        [:div (str msg)])])
        :disabled? (or errors?
                       (not unsaved-changes?)
                       saving?)
        :on-click  (fn [_]
                     (rf/dispatch [::events/save-changes {:close-modal! close-modal!}]))}
       (if saving? (loc/tr :saving) (loc/tr :save))]]]))

(defn view
  [{:keys [props]}]
  (let [{:keys [pipeline-attribute selected-field]} props
        state-atom (r/atom {})]
    (r/create-class
      {:component-did-mount
       (fn [_]
         (rf/dispatch [::events/init-options-data {:pipeline-attribute pipeline-attribute
                                                   :selected-field     selected-field}]))
       :component-will-unmount
       (fn [_]
         (rf/dispatch [::events/clear]))
       :reagent-render
       (fn [{:keys [shown? close-modal! topmost?]}]
         (let [unsaved-changes? @(rf/subscribe [::subs/has-new-changes?])]
           [modal/view
            {:visible?      (and shown? topmost?)
             :hide!         close-modal!
             :backdrop      "static"
             :size          ::modal-style/lg
             :close-button? false}
            (loc/tr :options-for
              [(loc/field-name selected-field
                               (registry/get-field-schema @session/tenant-schema
                                                          selected-field))])
            [modal-body
             {:pipeline-attribute   pipeline-attribute
              :selected-field       selected-field
              :state-atom           state-atom
              :close-modal!         close-modal!
              :selected-field-label (loc/field-name
                                      selected-field
                                      (registry/get-field-schema
                                        @session/tenant-schema
                                        selected-field))}]
            [modal-footer {:unsaved-changes? unsaved-changes?
                           :close-modal!     close-modal!}]]))})))
